import React, { useState } from 'react';
import StatTile from '../StatTile';
import { formatBytes, formatNumber, PALETTE } from '../charts';
import { Badge, ProgressBar } from '../ui';
import { SeriesCard } from './MetricCard';
import { fmt, rate } from './units';
import WorkspaceUsageModal from './WorkspaceUsageModal';

function TargetGroups({ groups }) {
  return (
    <div className="table-container">
      <table className="table table-stack">
        <thead>
          <tr>
            <th scope="col">Target group</th>
            <th scope="col">Serves</th>
            <th scope="col">Service</th>
            <th scope="col">Targets</th>
          </tr>
        </thead>
        <tbody>
          {groups.map(g => (
            <tr key={g.name}>
              <td data-label="Target group"><span className="mono text-sm">{g.name}</span></td>
              <td data-label="Serves">{g.path === 'default' ? 'everything else' : <span className="mono">{g.path}</span>}</td>
              <td data-label="Service">{g.service}</td>
              <td data-label="Targets">
                <span className="row gap-2">
                  <Badge tone="ok" dot>{g.healthy} healthy</Badge>
                  {g.unhealthy > 0 && <Badge tone="danger" dot>{g.unhealthy} unhealthy</Badge>}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Shards({ shards, onOpen }) {
  return (
    <div className="table-container">
      <table className="table table-stack">
        <thead>
          <tr>
            <th scope="col">Shard</th>
            <th scope="col">File system</th>
            <th scope="col" className="text-right">Workspaces</th>
            <th scope="col" className="text-right">Stored</th>
            <th scope="col" className="text-right">Throughput</th>
            <th scope="col">IO limit used</th>
          </tr>
        </thead>
        <tbody>
          {shards.map(s => (
            <tr key={s.id}>
              <td data-label="Shard" className="fw-600 num">
                {/* Opens the per-user breakdown of this one shard. */}
                <button type="button" className="btn btn-link num" onClick={() => onOpen(s)}
                        aria-label={`Workspace usage by user on shard ${s.shard}`}>
                  {s.shard}
                </button>
              </td>
              <td data-label="File system">
                <span className="mono text-xs">{s.id}</span>
                <span className="cell-sub truncate">{s.name}</span>
              </td>
              <td data-label="Workspaces" className="text-right num">{s.users ?? '—'}</td>
              <td data-label="Stored" className="text-right num">{formatBytes(s.size_bytes)}</td>
              <td data-label="Throughput" className="text-right num">{formatBytes(s.throughput_bps)}/s</td>
              <td data-label="IO limit used">
                <div className="usage-row compact">
                  <ProgressBar value={s.percent_io_limit} max={100} label={`IO limit used ${s.percent_io_limit}%`} />
                  <span className="usage-value num">{s.percent_io_limit}%</span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function EdgePanel({ data, range, loading }) {
  const c = data?.current || {};
  const shards = data?.efs || [];
  const ms = fmt('ms').value;
  // null: closed; {} all shards; a shard row: that shard alone.
  const [usageOf, setUsageOf] = useState(null);
  return (
    <>
      <div className="section-head">
        <h2>Load balancer</h2>
        <p className="mono">{data?.alb?.name || ''}</p>
      </div>
      <div className="dashboard-grid">
        <StatTile span={3} icon="globe" label="Requests" value={rate(c.requests_per_min || 0)}
          sub={`per minute, last complete ${data?.step_s >= 3600 ? 'hour' : data?.step_s >= 300 ? '5 min' : 'minute'}`} />
        <StatTile span={3} icon="clock" label="Response time" value={c.p95_ms != null ? ms(c.p95_ms) : '—'} sub="p95 at the load balancer" />
        <StatTile span={3} icon="alertCircle" label="Errors" value={formatNumber((c.http_5xx || 0) + (c.http_4xx || 0))}
          sub={`${c.http_5xx || 0} server · ${c.http_4xx || 0} client, last bucket`}
          tone={c.http_5xx ? 'negative' : undefined} />
        <StatTile span={3} icon="checkCircle" label="Targets" value={`${c.healthy_targets ?? '—'}`}
          sub={c.unhealthy_targets == null ? 'health not readable'
            : c.unhealthy_targets ? `${c.unhealthy_targets} unhealthy` : 'all healthy'}
          tone={c.unhealthy_targets == null ? undefined : c.unhealthy_targets ? 'negative' : 'positive'} />
      </div>
      <div className="dashboard-grid">
        <SeriesCard
          title="Requests" subtitle="Per minute, all target groups" unit="per_min" range={range} loading={loading}
          timestamps={data?.timestamps} series={data?.series}
          lines={[{ key: 'requests', label: 'Requests', color: PALETTE[0] }]}
        />
        <SeriesCard
          title="Response time" subtitle="p95 target response time" unit="ms" range={range} loading={loading}
          timestamps={data?.timestamps} series={data?.series}
          lines={[{ key: 'p95_ms', label: 'p95', color: PALETTE[3] }]}
        />
        <SeriesCard
          title="Error responses" subtitle="Client (4xx) and server (5xx) per bucket" range={range} loading={loading}
          timestamps={data?.timestamps} series={data?.series}
          lines={[
            { key: 'http_4xx', label: '4xx', color: PALETTE[6] },
            { key: 'http_5xx', label: '5xx', color: PALETTE[4] },
          ]}
          help="A 4xx is usually the client: an expired session, a missing route. A run of 5xx is ours, and the Services view shows which replica returned it."
          span={12}
        />
      </div>
      <div className="card card-flush mb-4">
        <TargetGroups groups={data?.alb?.target_groups || []} />
      </div>

      <div className="section-head">
        <h2>
          <button type="button" className="btn-link section-head-link" onClick={() => setUsageOf({})}>
            Agent workspaces
          </button>
        </h2>
        <p className="row gap-3">
          <span>
            {shards.length} EFS shards, one directory per user
            {shards.length > 0 && ` · ${formatBytes(shards.reduce((s, x) => s + x.size_bytes, 0))} stored`}
          </span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setUsageOf({})}>
            Usage by user
          </button>
        </p>
      </div>
      <div className="card card-flush mb-4">
        <Shards shards={shards} onOpen={s => setUsageOf(s)} />
      </div>
      <WorkspaceUsageModal
        open={usageOf !== null}
        onClose={() => setUsageOf(null)}
        fsId={usageOf?.id}
        shardLabel={usageOf?.id ? `shard ${usageOf.shard}` : undefined}
      />
    </>
  );
}
